import produce from 'immer';
import { registerReducer } from '../../../StateSetup/RootReducer';

const { BFF_URL, GBCORE_CLIENT_NAME, GBCORE_CLIENT_VERSION } = window.__ENV__;

// Action names
const ACTION_NAME_PREFIX = 'action/extendStudentStatus';
export const REQUEST_ACTION_NAME = `${ACTION_NAME_PREFIX}/request`;
export const RESPONSE_ACTION_NAME = `${ACTION_NAME_PREFIX}/response`;
export const ERROR_ACTION_NAME = `${ACTION_NAME_PREFIX}/error`;

/**
 * Fires an extendStudentStatus mutation when member wants to extend child's student status.
 * Family member query is fired after getting successful response from the extendStudentStatus mutation because when student status is extended, isStudentStatusExtendable
 * and eligibleForCoverage is calculated again in gb-core 2.0 with the new values from the mutation.
 * @param {object} familyMember
 * @param {boolean} [isChainedMutation] - indicates whether this mutation is part of a larger chain of
 * mutations, will commit immediately if false
 * @returns {Promise<object>}
 */
export const ExtendStudentStatus = (familyMember, isChainedMutation) => {
    return async (dispatch, getState, { logger }) => {
        // step 1: Request Action
        dispatch({ type: REQUEST_ACTION_NAME });

        const { jwt, sessionCorrelationId, language } = getState().Authentication;
        const transactionId = logger.generateGuid();

        logger.info(`${ACTION_NAME_PREFIX}: Updating student status. (personId: ${familyMember.personId})`, {
            correlation: { transactionId },
        });

        const extendStudentStatusMutation = await fetch(`${BFF_URL}/api/v2`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'gb-authorization': jwt,
                'mli-session-correlation-id': sessionCorrelationId,
                'mli-correlation-id': transactionId,
                'mli-channel': 'web',
                'gb-client-name': GBCORE_CLIENT_NAME,
                'gb-client-version': GBCORE_CLIENT_VERSION,
                'Accept-Language': language,
                ...(isChainedMutation && { 'no-commit': isChainedMutation }),
            },
            body: JSON.stringify({
                operationName: 'FamilyMemberExtendStudentStatus',
                variables: {
                    studentStatus: { personId: familyMember.personId },
                },
                query: `
                    mutation FamilyMemberExtendStudentStatus($studentStatus: ExtendStudentStatusInput){
                        extendStudentStatus(studentStatus:$studentStatus){
                            personId
                            status
                            inSchool
                        }
                    }
                     `,
            }),
        });

        const extendStudentStatusMutationResponse = await extendStudentStatusMutation.json();

        if (extendStudentStatusMutationResponse.errors) {
            const { message, code } = extendStudentStatusMutationResponse.errors[0];
            logger.error(`${ACTION_NAME_PREFIX}: Error updating student status. Code: ${code} Reason: ${message}`, {
                correlation: { transactionId },
            });
            return dispatch({
                type: ERROR_ACTION_NAME,
                code,
                message,
            });
        }

        return dispatch({
            type: RESPONSE_ACTION_NAME,
            success: true,
        });
    };
};

/**
 * Dispatched before the gb-core-2 requests are sent to indicate updating and reset error state.
 */
export const requestReducer = produce((state) => {
    state.Errors.familyMemberUpdate.hasError = false;
    state.MemberData.isExtendCoverageDataSaving = true;
});

/**
 * When we get a successful response, updates dependent's inSchool, status, isStudentStatusExtendable and eligibleForCoverage under benefits in the state.
 */
export const responseReducer = produce((state) => {
    state.Errors.familyMemberUpdate.hasError = false;
    state.MemberData.isExtendCoverageDataSaving = false;
});

/**
 * Called when either call to gb-core-2 fails so we can display an error to the user.
 */
export const errorReducer = produce((state, action) => {
    state.MemberData.isExtendCoverageDataSaving = false;
    state.Errors.familyMemberUpdate.hasError = true;
    state.Errors.familyMemberUpdate.code = action.code;
    state.Errors.familyMemberUpdate.message = action.message;
});

// Register Reducers with Redux root reducer
registerReducer(REQUEST_ACTION_NAME, requestReducer);
registerReducer(RESPONSE_ACTION_NAME, responseReducer);
registerReducer(ERROR_ACTION_NAME, errorReducer);
